import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, MapPin, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import logo from "@/assets/nivexa_icon_80.webp";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useServiceStatus } from "@/components/OnboardingPaused";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Product", path: "/product" },
  
  { label: "Pricing", path: "/pricing" },
  { label: "Education Hub", path: "/blog" },
  { label: "FAQ", path: "/faq" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { paused } = useServiceStatus();

  return (
    <nav
      className={cn(
        "fixed left-0 right-0 z-50 transition-all duration-300",
        paused ? "top-9 sm:top-10" : "top-0",
        scrolled
          ? "bg-card/80 backdrop-blur-2xl border-b border-border/40 shadow-[0_4px_30px_hsl(var(--primary)/0.05)]"
          : "bg-transparent border-b border-transparent"
      )}
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src={logo} alt="Nivexa FX Logo" width={120} height={120} className="h-[84px] w-[84px] object-contain transition-transform duration-300 group-hover:scale-105" fetchPriority="high" />
          <span className="font-display font-bold text-lg tracking-wide text-foreground hidden sm:inline">NivexA <span className="text-primary">FX</span></span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-0.5">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
                className={cn(
                  "relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
            >
              {link.label}
              {location.pathname === link.path && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 w-5 rounded-full bg-primary" />
              )}
            </Link>
          ))}
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Dubai location badge */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground border border-border/40 rounded-full px-3 py-1.5 bg-secondary/30">
            <span className="text-sm leading-none">🇦🇪</span>
            <span>Dubai</span>
          </div>
          <LanguageSwitcher />
          <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan rounded-lg font-semibold">
            <Link to="/pricing">Try Demo</Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-lg font-semibold border-border/50 hover:border-primary/50">
            <Link to="/resources">Login</Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-1">
          <LanguageSwitcher />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-card/95 backdrop-blur-2xl border-border w-72">
              <SheetTitle className="text-foreground font-display text-lg">Nivexa FX</SheetTitle>
              {/* Dubai badge mobile */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 mb-4">
                <span className="text-sm">🇦🇪</span>
                <MapPin className="h-3 w-3" />
                <span>Dubai, UAE</span>
              </div>
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                      location.pathname === link.path
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-border/50 mt-4 pt-4 flex flex-col gap-2">
                  <Button asChild className="bg-primary text-primary-foreground glow-cyan"><Link to="/pricing" onClick={() => setOpen(false)}>Try Demo</Link></Button>
                  <Button asChild variant="outline" className="border-border/50"><Link to="/resources" onClick={() => setOpen(false)}>Login</Link></Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
